// AGC: UnifiedInventoryPage - Single unified page for inventory and inputs management
// AGC: Combines InventoryList (real stock) and InputsPage (task requirements)
// AGC: Shows all items in one place with accordion view by category
// AGC: Features: stock management, purchase orders, movement history, task-based requirements

import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from '../../hooks/useTranslation'
import { useInventory } from '../../hooks/useInventory'
import { usePullToRefresh } from '../../hooks/usePullToRefresh'
import { useHasPermission } from '../../hooks/useRBAC'
import { useLotes, useFarms } from '../../context'
import { useAuth } from '../../hooks/useAuth'
import { persistCustomList } from '../../hooks/useCustomList'
import { fetchTaskTemplates } from '../../services/bomService'
import { clampDecimals } from '../../utils'
import { useTasksQuery } from '../../services/tasks'
import type { QuoteTranslations, TaskQuoteOption } from '../../services/quotePdfService'
import {
  INPUT_CATEGORIES,
  getInputCategoryInfo,
  normalizeCategory,
  getStockStatus,
  type StockStatus,
  type InputCategory,
  type BOMItem,
  type InventoryItem,
} from '../../types/inventory'
import type { TaskTemplate, TaskInput } from '../../types/admin/models'
import type { Task } from '../../types/task'
import type { Plot, Screen } from '../../types'
import { 
  Package, 
  Search,
  RefreshCw,
  Check,
  AlertTriangle,
  X,
  ShoppingCart,
  ChevronDown,
  Filter,
  Share2,
  ArrowUpCircle,
  Camera,
  Loader2,
  ChevronUp,
  ListOrdered,
  LayoutGrid,
  Plus,
  Database,
  ClipboardList,
  Layers,
} from 'lucide-react'
import { TopBar } from '../shared'
import { CategoryAccordion, type CategoryInputItem } from '../inputs/CategoryAccordion'
import { OrderBuilderModal } from '../inputs/OrderBuilderModal'
import { AddStockModal, type StockEntryItem } from '../fields/AddStockModal'
import { ScanInvoiceModal } from '../fields/ScanInvoiceModal'
import { SupplyDetailModal } from '../supplies'
import { QuoteBuilderModal } from './QuoteBuilderModal'
import { ManageCategoriesUnitsModal, type CustomListItem } from './ManageCategoriesUnitsModal'
import { getPendingOrdersSummary } from '../../services/purchaseOrderService'
import type { PendingOrderSummary } from '../../types/inventory'
import toast from 'react-hot-toast'
// AGC: Warehouse / Silo / Tank imports
import { useWarehouse } from '../../hooks/useWarehouse'
import { WarehouseSelector } from './WarehouseSelector'
import { SiloTankSection } from './SiloTankSection'
import { NecessarioBreakdownSheet, type NecessarioTaskRow } from './NecessarioBreakdownSheet'
import { ManageWarehousesModal } from './ManageWarehousesModal'
import { TransferStockModal } from './TransferStockModal'

// AGC: Built-in units for register product; custom units can be added via manage modal
const BUILT_IN_UNITS: CustomListItem[] = [
  { id: 'L', label: 'L' },
  { id: 'kg', label: 'kg' },
  { id: 'g', label: 'g' },
  { id: 'sacas', label: 'Sacas' },
  { id: 'unidades', label: 'Unidades' },
  { id: 'doses', label: 'Doses' },
]

const STORAGE_KEY_CUSTOM_CATEGORIES = 'droidfarm_custom_categories'
const STORAGE_KEY_CUSTOM_UNITS = 'droidfarm_custom_units'

// =============================================================================
// AGC: TYPES
// =============================================================================

interface UnifiedInventoryPageProps {
  onBack?: () => void
  onNavigate?: (screen: Screen) => void
  autoOpenScanModal?: boolean // AGC: Voice command trigger to auto-open camera scan modal
}

// AGC: Source type to identify where the item comes from
type ItemSource = 'inventory' | 'requirement' | 'both'

// AGC: Unified item that combines inventory and requirements
interface UnifiedInventoryItem {
  // AGC: Identification
  id: string
  inputId?: string
  name: string
  
  // AGC: Category
  category: InputCategory
  
  // AGC: Stock (from inventory)
  inventoryItemId?: string
  inStock: number
  minimumStock?: number
  maximumStock?: number
  
  // AGC: Requirements (from tasks)
  totalRequired?: number
  needToBuy?: number
  lotsNeeding?: { id: string; name: string; required: number }[]
  tasks?: string[]
  
  // AGC: Status
  status: StockStatus
  source: ItemSource
  
  // AGC: Metadata
  unit: string
  location?: string
  costPerUnit?: number
  lastPurchasePrice?: number
}

// AGC: Aggregated input from tasks
interface AggregatedInput {
  inputId: string
  name: string
  category: InputCategory
  unit: string
  totalRequired: number
  inStock: number
  needToBuy: number
  status: StockStatus
  tasks: string[]
  lotsNeeding: { id: string; name: string; required: number }[]
}

type ViewMode = 'accordion' | 'list'
type SourceFilter = 'all' | 'inventory' | 'requirement'

// =============================================================================
// AGC: HELPER FUNCTIONS
// =============================================================================

// AGC: Smart number formatter — no trailing .0 for whole numbers, 1-2 decimals otherwise
const formatQty = (n: number): string => {
  if (!isFinite(n)) return '0'
  if (Math.abs(n) >= 100) return Math.round(n).toString()
  if (Number.isInteger(n)) return n.toString()
  return n.toFixed(Math.abs(n) < 10 ? 2 : 1).replace(/\.?0+$/, '')
}

const getStatusInfo = (status: StockStatus) => {
  const config: Record<StockStatus, { label: string; color: string; bgColor: string; icon: React.ReactNode }> = {
    ok: {
      label: 'OK',
      color: 'text-emerald-700',
      bgColor: 'bg-emerald-50',
      icon: <Check className="w-3.5 h-3.5" />
    },
    low: {
      label: 'BAIXO',
      color: 'text-amber-700',
      bgColor: 'bg-amber-50',
      icon: <AlertTriangle className="w-3.5 h-3.5" />
    },
    critical: {
      label: 'COMPRAR',
      color: 'text-red-700',
      bgColor: 'bg-red-50',
      icon: <AlertTriangle className="w-3.5 h-3.5" />
    },
    out: {
      label: 'COMPRAR',
      color: 'text-red-700',
      bgColor: 'bg-red-50',
      icon: <X className="w-3.5 h-3.5" />
    },
    overstock: {
      label: 'ALTO',
      color: 'text-blue-700',
      bgColor: 'bg-blue-50',
      icon: <Check className="w-3.5 h-3.5" />
    },
  }
  return config[status]
}

// AGC: Source badge configuration
const getSourceInfo = (source: ItemSource) => {
  const config: Record<ItemSource, { label: string; color: string; bgColor: string; icon: React.ReactNode }> = {
    inventory: {
      label: 'Inventário',
      color: 'text-blue-700',
      bgColor: 'bg-blue-50',
      icon: <Database className="w-3 h-3" />
    },
    requirement: {
      label: 'Requerido',
      color: 'text-purple-700',
      bgColor: 'bg-purple-50',
      icon: <ClipboardList className="w-3 h-3" />
    },
    both: {
      label: 'Ambos',
      color: 'text-emerald-700',
      bgColor: 'bg-emerald-50',
      icon: <Layers className="w-3 h-3" />
    },
  }
  return config[source]
}

// AGC: Normalize name for comparison
const normalizeName = (name: string): string => {
  return name.toLowerCase().replace(/[^a-z0-9]/g, '')
}

// AGC: Calculate status considering both stock and requirements
const calculateStatusWithRequirements = (
  inStock: number,
  totalRequired: number | undefined,
  minimumStock: number | undefined
): StockStatus => {
  // AGC: If there are requirements, use requirement-based status
  if (totalRequired !== undefined && totalRequired > 0) {
    if (inStock === 0) return 'out'
    if (inStock < totalRequired * 0.5) return 'critical'
    if (inStock < totalRequired) return 'low'
    if (inStock > totalRequired * 1.5) return 'overstock'
    return 'ok'
  }
  
  // AGC: Otherwise use minimum stock based status
  return getStockStatus(inStock, minimumStock || 0, undefined)
}

// AGC: Convert UnifiedInventoryItem to BOMItem for modals
const toBOMItem = (item: UnifiedInventoryItem): BOMItem => ({
  inputId: item.inputId || item.id,
  inputName: item.name,
  category: item.category,
  unit: item.unit,
  requiredQuantity: item.totalRequired || 0,
  currentStock: item.inStock,
  needToBuy: item.needToBuy || 0,
  stockStatus: item.status,
  estimatedCost: (item.costPerUnit || 0) * (item.needToBuy || 0),
  tasks: item.tasks || [],
})

// =============================================================================
// AGC: MERGE FUNCTION - Combines inventory and requirements
// =============================================================================

function mergeInventoryAndRequirements(
  inventoryItems: InventoryItem[],
  aggregatedInputs: AggregatedInput[]
): UnifiedInventoryItem[] {
  const unifiedMap = new Map<string, UnifiedInventoryItem>()
  
  // AGC: First add all inventory items
  inventoryItems.forEach(item => {
    const key = item.inputId || normalizeName(item.name)
    unifiedMap.set(key, {
      id: item.id,
      inputId: item.inputId,
      name: item.name,
      category: normalizeCategory(String(item.category || 'other')),
      inventoryItemId: item.id,
      inStock: item.quantity,
      minimumStock: item.minimumStock,
      maximumStock: item.maximumStock,
      status: getStockStatus(item.quantity, item.minimumStock || 0, item.maximumStock),
      source: 'inventory',
      unit: item.unit,
      location: item.location,
      costPerUnit: item.costPerUnit,
      lastPurchasePrice: item.lastPurchasePrice,
    })
  })
  
  // AGC: Then merge with requirements
  aggregatedInputs.forEach(req => {
    const key = req.inputId || normalizeName(req.name)
    const existing = unifiedMap.get(key)
    
    if (existing) {
      // AGC: Item exists in inventory - merge with requirements
      existing.totalRequired = req.totalRequired
      existing.needToBuy = Math.max(0, req.totalRequired - existing.inStock)
      existing.lotsNeeding = req.lotsNeeding
      existing.tasks = req.tasks
      existing.source = 'both'
      // AGC: Recalculate status considering requirements
      existing.status = calculateStatusWithRequirements(
        existing.inStock,
        req.totalRequired,
        existing.minimumStock
      )
    } else {
      // AGC: Only requirement - add as new item
      unifiedMap.set(key, {
        id: req.inputId || key,
        inputId: req.inputId,
        name: req.name,
        category: req.category,
        inStock: 0,
        totalRequired: req.totalRequired,
        needToBuy: req.needToBuy,
        lotsNeeding: req.lotsNeeding,
        tasks: req.tasks,
        status: req.status,
        source: 'requirement',
        unit: req.unit,
      })
    }
  })
  
  return Array.from(unifiedMap.values())
}

// =============================================================================
// AGC: MAIN COMPONENT
// =============================================================================

export const UnifiedInventoryPage: React.FC<UnifiedInventoryPageProps> = ({ onBack, onNavigate, autoOpenScanModal }) => {
  const { t, i18n } = useTranslation()
  const { user, getAuthToken } = useAuth()
  const { currentFarm } = useFarms()
  
  // AGC: Data hooks
  const { 
    items: inventoryItems, 
    loading: loadingInventory, 
    refresh: refreshInventory, 
    recordMovement, 
    addItem 
  } = useInventory()
  const { plots: lots, isLoading: loadingLots, refreshPlots } = useLotes()

  // AGC: v3.10.4 — tasks list is used by aggregatedInputs to subtract "Necessário"
  // AGC: contributions from templates whose corresponding task is already done.
  const { data: allTasks, refetch: refetchTasks } = useTasksQuery()

  const canManageInventory = useHasPermission('canManageInventory')
  const canConsumeInventory = useHasPermission('canConsumeInventory')

  // AGC: Pull-to-refresh
  const { scrollRef, pullDistance, isRefreshing, pullThreshold, touchHandlers } = usePullToRefresh(
    async () => { await refreshInventory() }
  )

  // AGC: Local state
  const [selectedLotId, setSelectedLotId] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [selectedSource, setSelectedSource] = useState<SourceFilter>('all')
  const [showFilters, setShowFilters] = useState(false)
  const [refreshCounter, setRefreshCounter] = useState(0)
  const [viewMode, setViewMode] = useState<ViewMode>('accordion')
  // AGC: Status filter from dashboard badges (null = show all)
  const [statusFilter, setStatusFilter] = useState<'ok' | 'low' | 'critical' | null>(null)
  // AGC: "In stock only" flat table toggle
  const [inStockOnly, setInStockOnly] = useState(false)
  
  // AGC: Accordion expansion state
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())
  
  // AGC: Task templates cache (keyed by cropId)
  const [taskTemplatesCache, setTaskTemplatesCache] = useState<Record<string, TaskTemplate[]>>({})
  const [loadingTemplates, setLoadingTemplates] = useState(false)
  
  // AGC: Modal states
  const [isAddStockModalOpen, setIsAddStockModalOpen] = useState(false)
  const [isRegisterProductOpen, setIsRegisterProductOpen] = useState(false)
  const [isScanInvoiceModalOpen, setIsScanInvoiceModalOpen] = useState(false)
  
  // AGC: Auto-open scan modal when triggered by voice command
  useEffect(() => {
    if (autoOpenScanModal) {
      setIsScanInvoiceModalOpen(true)
    }
  }, [autoOpenScanModal])
  
  const [isOrderBuilderOpen, setIsOrderBuilderOpen] = useState(false)
  const [pendingOrdersMap, setPendingOrdersMap] = useState<Map<string, PendingOrderSummary>>(new Map())
  const [isQuoteBuilderOpen, setIsQuoteBuilderOpen] = useState(false)
  const [isFabMenuOpen, setIsFabMenuOpen] = useState(false)
  
  // AGC: Supply detail modal state
  const [selectedSupplyItem, setSelectedSupplyItem] = useState<UnifiedInventoryItem | null>(null)
  const [isSupplyDetailOpen, setIsSupplyDetailOpen] = useState(false)

  // AGC: Warehouse / Silo / Tank state
  const warehouseHook = useWarehouse()
  const { warehouses, containers, hasMultipleWarehouses, selectedWarehouseFilter, setWarehouseFilter } = warehouseHook
  const [isManageWarehousesOpen, setIsManageWarehousesOpen] = useState(false)
  const [isTransferStockOpen, setIsTransferStockOpen] = useState(false)

  // AGC: Register new product form (name, category, unit, price — not stock); category/unit can be custom
  const [registerProductForm, setRegisterProductForm] = useState<{
    name: string
    category: string
    unit: string
    initialQuantity: number
    minimumStock: number
    costPerUnit: number
  }>({
    name: '',
    category: 'other',
    unit: 'L',
    initialQuantity: 0,
    minimumStock: 0,
    costPerUnit: 0,
  })
  const [isRegisterProductSubmitting, setIsRegisterProductSubmitting] = useState(false)

  // AGC: Custom categories/units (persisted per farm in localStorage); manage modal mode
  const [customCategories, setCustomCategories] = useState<CustomListItem[]>([])
  const [customUnits, setCustomUnits] = useState<CustomListItem[]>([])
  const [manageCategoriesUnitsMode, setManageCategoriesUnitsMode] = useState<'categories' | 'units' | null>(null)
  const [registerCategoryDropdownOpen, setRegisterCategoryDropdownOpen] = useState(false)
  const [registerUnitDropdownOpen, setRegisterUnitDropdownOpen] = useState(false)
  const registerCategoryDropdownRef = useRef<HTMLDivElement>(null)
  const registerUnitDropdownRef = useRef<HTMLDivElement>(null)

  const farmIdForStorage = currentFarm?.id || 'default'

  // AGC: Close register-product dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (registerCategoryDropdownOpen && registerCategoryDropdownRef.current && !registerCategoryDropdownRef.current.contains(target))
        setRegisterCategoryDropdownOpen(false)
      if (registerUnitDropdownOpen && registerUnitDropdownRef.current && !registerUnitDropdownRef.current.contains(target))
        setRegisterUnitDropdownOpen(false)
    }
    if (registerCategoryDropdownOpen || registerUnitDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [registerCategoryDropdownOpen, registerUnitDropdownOpen])

  // AGC: Load custom categories/units from localStorage when farm changes
  useEffect(() => {
    try {
      const rawCat = localStorage.getItem(`${STORAGE_KEY_CUSTOM_CATEGORIES}_${farmIdForStorage}`)
      const rawUnit = localStorage.getItem(`${STORAGE_KEY_CUSTOM_UNITS}_${farmIdForStorage}`)
      if (rawCat) {
        const parsed = JSON.parse(rawCat) as CustomListItem[]
        if (Array.isArray(parsed)) setCustomCategories(parsed)
      }
      if (rawUnit) {
        const parsed = JSON.parse(rawUnit) as CustomListItem[]
        if (Array.isArray(parsed)) setCustomUnits(parsed)
      }
    } catch {
      // AGC: Ignore invalid stored data
    }
  }, [farmIdForStorage])

  const saveCustomCategories = useCallback((list: CustomListItem[]) => {
    setCustomCategories(list)
    persistCustomList(STORAGE_KEY_CUSTOM_CATEGORIES, farmIdForStorage, list, user?.uid)
  }, [farmIdForStorage, user?.uid])

  const saveCustomUnits = useCallback((list: CustomListItem[]) => {
    setCustomUnits(list)
    persistCustomList(STORAGE_KEY_CUSTOM_UNITS, farmIdForStorage, list, user?.uid)
  }, [farmIdForStorage, user?.uid])

  // AGC: Locale-aware category labels for the register product dropdown
  const currentLang = i18n?.language || 'pt'
  const builtInCategoriesForModal = useMemo(
    () => INPUT_CATEGORIES.map((c) => ({
      id: c.id,
      label: currentLang === 'es' ? c.nameEs : currentLang === 'en' ? c.nameEn : c.namePt,
    })),
    [currentLang]
  )

  const handleAddCustomCategory = useCallback(
    (item: CustomListItem) => {
      if (customCategories.some((c) => c.id === item.id)) return
      saveCustomCategories([...customCategories, item])
    },
    [customCategories, saveCustomCategories]
  )
  const handleEditCustomCategory = useCallback(
    (oldId: string, item: CustomListItem) => {
      saveCustomCategories(
        customCategories.map((c) => (c.id === oldId ? { ...c, ...item } : c))
      )
    },
    [customCategories, saveCustomCategories]
  )
  const handleRemoveCustomCategory = useCallback(
    (id: string) => {
      saveCustomCategories(customCategories.filter((c) => c.id !== id))
    },
    [customCategories, saveCustomCategories]
  )

  const handleAddCustomUnit = useCallback(
    (item: CustomListItem) => {
      if (customUnits.some((u) => u.id === item.id)) return
      saveCustomUnits([...customUnits, item])
    },
    [customUnits, saveCustomUnits]
  )
  const handleEditCustomUnit = useCallback(
    (oldId: string, item: CustomListItem) => {
      saveCustomUnits(customUnits.map((u) => (u.id === oldId ? { ...u, ...item } : u)))
    },
    [customUnits, saveCustomUnits]
  )
  const handleRemoveCustomUnit = useCallback(
    (id: string) => {
      saveCustomUnits(customUnits.filter((u) => u.id !== id))
    },
    [customUnits, saveCustomUnits]
  )
  
  // AGC: Get active lots (only those with crop types defined)
  const activeLots = useMemo(() => {
    if (!lots || !Array.isArray(lots)) return []
    return lots.filter(lot => lot.cropType && lot.area && lot.area > 0)
  }, [lots])
  
  // AGC: Get unique crop types from active lots
  const uniqueCropTypes = useMemo(() => {
    const crops = new Set<string>()
    activeLots.forEach(lot => {
      if (lot.cropType) crops.add(lot.cropType)
    })
    return Array.from(crops)
  }, [activeLots])
  
  // AGC: Fetch task templates for all unique crop types. When force=true, skip the
  // AGC: cache-hit shortcut so a pull-to-refresh pulls a fresh copy atomically (old
  // AGC: cache stays in place until the new data arrives — no empty-cache window).
  const fetchAllTemplates = useCallback(async (force: boolean = false) => {
    if (uniqueCropTypes.length === 0) return

    setLoadingTemplates(true)
    try {
      const token = await getAuthToken()
      const newCache: Record<string, TaskTemplate[]> = {}

      await Promise.all(
        uniqueCropTypes.map(async (cropId) => {
          if (!force && taskTemplatesCache[cropId]) {
            newCache[cropId] = taskTemplatesCache[cropId]
            return
          }

          try {
            const templates = await fetchTaskTemplates(cropId, token)
            newCache[cropId] = templates
          } catch (error) {
            console.error(`AGC: Error fetching templates for ${cropId}:`, error)
            newCache[cropId] = force && taskTemplatesCache[cropId] ? taskTemplatesCache[cropId] : []
          }
        })
      )

      setTaskTemplatesCache(prev => ({ ...prev, ...newCache }))
    } catch (error) {
      console.error('AGC: Error fetching task templates:', error)
    } finally {
      setLoadingTemplates(false)
    }
  }, [uniqueCropTypes, getAuthToken, taskTemplatesCache])
  
  useEffect(() => {
    if (uniqueCropTypes.length > 0 && !loadingLots) {
      fetchAllTemplates()
    }
  }, [uniqueCropTypes, loadingLots]) // eslint-disable-line react-hooks/exhaustive-deps
  
  // AGC: Fetch pending orders for all inputs
  const fetchPendingOrders = useCallback(async () => {
    if (!currentFarm?.id || !user?.uid) return
    
    try {
      const summary = await getPendingOrdersSummary(user.uid, currentFarm.id)
      setPendingOrdersMap(summary)
    } catch (error) {
      console.error('AGC: Error fetching pending orders:', error)
    }
  }, [currentFarm?.id, user?.uid])
  
  useEffect(() => {
    if (currentFarm?.id && user?.uid) {
      fetchPendingOrders()
    }
  }, [currentFarm?.id, user?.uid, fetchPendingOrders])

  // AGC: Count unique open pedidos across all pending-order buckets (same order can appear
  // AGC: under several inputIds). Drives the counter on the toolbar "Pedidos" button.
  const openOrdersCount = useMemo(() => {
    const ids = new Set<string>()
    for (const [, summary] of pendingOrdersMap) {
      for (const ref of summary.orders) ids.add(ref.orderId)
    }
    return ids.size
  }, [pendingOrdersMap])
  
  // AGC: v3.10.4 — Build a per-plot set of completed-task titles. When a real task
  // AGC: on a plot is done/completed, its matching TEMPLATE task should drop out of
  // AGC: the "Necessário" aggregation — the need was satisfied, the heads-up should reflect that.
  // AGC: Title-based match (normalised). Edge case: renamed tasks won't match; those stay in the aggregation until cycle end.
  const completedTemplateTitlesByPlot = useMemo<Map<string, Set<string>>>(() => {
    const map = new Map<string, Set<string>>()
    if (!Array.isArray(allTasks)) return map
    for (const t of allTasks) {
      const status = String((t as { status?: string }).status || '').toLowerCase()
      if (status !== 'done' && status !== 'completed') continue
      const plotId = (t as { plotId?: string | null }).plotId
      const title = (t as { title?: string }).title
      if (!plotId || !title) continue
      const norm = normalizeName(title)
      if (!map.has(plotId)) map.set(plotId, new Set())
      map.get(plotId)!.add(norm)
    }
    return map
  }, [allTasks])

  // AGC: Look up category from template cache when task input has 'unknown'/'other'
  const lookupTemplateCat = useCallback((inputId?: string, plotId?: string): InputCategory | null => {
    if (!inputId) return null
    const lot = plotId ? activeLots.find(l => l.id === plotId) : undefined
    const cropTypes = lot?.cropType ? [lot.cropType] : Object.keys(taskTemplatesCache)
    for (const ct of cropTypes) {
      for (const tmpl of (taskTemplatesCache[ct] || [])) {
        for (const ti of (tmpl.inputs || [])) {
          if (ti.inputId === inputId && ti.category) return ti.category as InputCategory
        }
      }
    }
    return null
  }, [activeLots, taskTemplatesCache])

  // AGC: Aggregate input requirements from all lots using task templates
  const aggregatedInputs = useMemo<AggregatedInput[]>(() => {
    const inputMap = new Map<string, AggregatedInput>()

    const lotsToProcess = selectedLotId === 'all'
      ? activeLots
      : activeLots.filter(lot => lot.id === selectedLotId)

    lotsToProcess.forEach((lot: Plot) => {
      if (!lot.cropType || !lot.area) return

      const templates = taskTemplatesCache[lot.cropType] || []
      const doneTitles = completedTemplateTitlesByPlot.get(lot.id)

      templates.forEach((task: TaskTemplate) => {
        if (!task.inputs || task.inputs.length === 0) return
        // AGC: v3.10.4 — skip templates that have a completed real-task counterpart on this plot.
        if (doneTitles && doneTitles.has(normalizeName(task.title))) return

        task.inputs.forEach((input: TaskInput) => {
          const key = input.inputId || `${input.inputName}-${input.category}`
          const totalQty = input.quantityPerHa * (lot.area || 0)
          
          if (inputMap.has(key)) {
            const existing = inputMap.get(key)!
            existing.totalRequired += totalQty
            if (!existing.tasks.includes(task.title)) {
              existing.tasks.push(task.title)
            }
            const existingLot = existing.lotsNeeding.find(l => l.id === lot.id)
            if (existingLot) {
              existingLot.required += totalQty
            } else {
              existing.lotsNeeding.push({
                id: lot.id,
                name: lot.name || 'Sem nome',
                required: totalQty,
              })
            }
          } else {
            inputMap.set(key, {
              inputId: input.inputId || key,
              name: input.inputName,
              category: normalizeCategory(String(input.category || 'other')),
              unit: input.unit,
              totalRequired: totalQty,
              inStock: 0,
              needToBuy: 0,
              status: 'out',
              tasks: [task.title],
              lotsNeeding: [{
                id: lot.id,
                name: lot.name || 'Sem nome',
                required: totalQty,
              }],
            })
          }
        })
      })
    })

    // AGC: Also include inputs from actual pending/in-progress tasks
    if (Array.isArray(allTasks)) {
      for (const task of allTasks as Array<{
        id: string; title?: string; status?: string; plotId?: string
        inputs?: Array<{ inputId?: string; inputName?: string; quantityPerHa?: number; totalQuantity?: number; unit?: string; category?: string; inputSnapshot?: { category?: string } }>
      }>) {
        const status = String(task.status || '').toLowerCase()
        if (status === 'done' || status === 'completed' || status === 'cancelled') continue
        const inputs = task.inputs || []
        if (inputs.length === 0) continue

        for (const inp of inputs) {
          if (!inp.inputId && !inp.inputName) continue
          // AGC: category from task input → inputSnapshot → template lookup → 'other'
          const rawCat = inp.category || inp.inputSnapshot?.category || ''
          let cat = rawCat ? normalizeCategory(rawCat) : '' as InputCategory
          if (!cat || cat === 'unknown' as InputCategory || cat === 'other') {
            const tmplCat = lookupTemplateCat(inp.inputId, task.plotId)
            if (tmplCat) cat = tmplCat
            else if (!cat) cat = 'other' as InputCategory
          }
          const key = inp.inputId || `${inp.inputName}-${cat}`
          const totalQty = Number(inp.totalQuantity) || 0
          if (totalQty <= 0) continue

          if (inputMap.has(key)) {
            const existing = inputMap.get(key)!
            existing.totalRequired += totalQty
            if (!existing.tasks.includes(task.title || '')) {
              existing.tasks.push(task.title || '')
            }
          } else {
            inputMap.set(key, {
              inputId: inp.inputId || key,
              name: inp.inputName || '',
              category: cat,
              unit: inp.unit || 'un',
              totalRequired: totalQty,
              inStock: 0,
              needToBuy: 0,
              status: 'out',
              tasks: [task.title || ''],
              lotsNeeding: [],
            })
          }
        }
      }
    }

    // AGC: Update inStock from inventory and calculate needToBuy
    const result = Array.from(inputMap.values()).map(input => {
      const normalizedInputName = normalizeName(input.name)
      
      // AGC: Filter all matches, then pick the one with the highest stock.
      // AGC: Duplicate items with qty=0 exist from legacy receipt code; prefer the real one.
      const candidates = inventoryItems.filter(item => {
        if (item.inputId && input.inputId && item.inputId === input.inputId) {
          return true
        }
        const normalizedItemName = normalizeName(item.name)
        const exactMatch = normalizedItemName === normalizedInputName
        const itemContainsInput = normalizedItemName.includes(normalizedInputName)
        const inputContainsItem = normalizedInputName.includes(normalizedItemName)
        return exactMatch || itemContainsInput || inputContainsItem
      })
      const matchingItem = candidates.length > 1
        ? candidates.reduce((best, c) => ((c.quantity || 0) > (best.quantity || 0) ? c : best), candidates[0])
        : candidates[0]
      
      // AGC: v3.10.3 — For fuel items with tanks/silos assigned, read the sum of
      // AGC: container levels instead of InventoryItem.quantity. Containers are the
      // AGC: ground truth for physical stock; inventory.quantity can drift or be 0
      // AGC: when no InventoryItem doc exists yet. Diesel-scoped (fuel categories only)
      // AGC: so non-fuel behaviour is unchanged.
      const FUEL_CATS = new Set(['fuel', 'combustivel', 'combustível', 'diesel', 'gasolina', 'etanol'])
      const inputCatLC = String(input.category || '').toLowerCase()
      const itemCatLC = String(matchingItem?.category || '').toLowerCase()
      const isFuel = FUEL_CATS.has(inputCatLC) || FUEL_CATS.has(itemCatLC)
      let inStock = matchingItem?.quantity || 0
      if (isFuel && matchingItem) {
        const assignedTanks = (containers || []).filter(c => c.assignedProductId === matchingItem.id)
        if (assignedTanks.length > 0) {
          inStock = assignedTanks.reduce((s, c) => s + (c.currentLevel || 0), 0)
        }
      }
      const needToBuy = Math.max(0, input.totalRequired - inStock)

      let status: StockStatus = 'ok'
      if (inStock === 0) {
        status = 'out'
      } else if (inStock < input.totalRequired * 0.5) {
        status = 'critical'
      } else if (inStock < input.totalRequired) {
        status = 'low'
      } else if (inStock > input.totalRequired * 1.5) {
        status = 'overstock'
      }

      return { ...input, inStock, needToBuy, status }
    })

    return result
  }, [activeLots, selectedLotId, inventoryItems, taskTemplatesCache, containers, completedTemplateTitlesByPlot, allTasks, lookupTemplateCat])

  // AGC: Map inventory-item-id → expected production this cycle (Phase 1 grain-output spec).
  // AGC: Sums expectedTotalQuantity from all non-completed tasks that have outputs[].
  // AGC: Keys off inventoryItemId (set by backend at task creation — empty string for not-yet-created).
  const previsaoByProductId = useMemo(() => {
    const map = new Map<string, number>()
    if (!Array.isArray(allTasks)) return map
    for (const t of allTasks) {
      const status = String((t as { status?: string }).status || '').toLowerCase()
      if (status === 'done' || status === 'completed') continue
      const outputs = (t as { outputs?: Array<{ inventoryItemId?: string; expectedTotalQuantity?: number }> }).outputs || []
      for (const o of outputs) {
        if (!o.inventoryItemId || !o.expectedTotalQuantity) continue
        map.set(o.inventoryItemId, (map.get(o.inventoryItemId) || 0) + o.expectedTotalQuantity)
      }
    }
    return map
  }, [allTasks])

  // AGC: v3.12.5 — Per-task breakdown of Necessário for drill-down on the tank card.
  // AGC: Keyed by inventory item id. Each entry is the list of pending tasks that
  // AGC: contribute the number, with editable L/ha. Uses allTasks directly so the
  // AGC: sheet can PUT targeted edits back to the task.
  const necessarioBreakdownByProductId = useMemo(() => {
    const map = new Map<string, NecessarioTaskRow[]>()
    if (!Array.isArray(allTasks)) return map

    const inputIdToInvItems = new Map<string, typeof inventoryItems>()
    const nameToInvItems = new Map<string, typeof inventoryItems>()
    for (const item of inventoryItems) {
      if (item.inputId) {
        const bucket = inputIdToInvItems.get(item.inputId) || []
        bucket.push(item)
        inputIdToInvItems.set(item.inputId, bucket)
      }
      if (item.name) {
        const key = normalizeName(item.name)
        const bucket = nameToInvItems.get(key) || []
        bucket.push(item)
        nameToInvItems.set(key, bucket)
      }
    }

    const plotById = new Map<string, { name?: string; area?: number }>()
    for (const lot of activeLots as Array<{ id: string; name?: string; area?: number }>) {
      plotById.set(lot.id, { name: lot.name, area: lot.area })
    }

    for (const t of allTasks as Array<{
      id: string
      title?: string
      category?: string
      plotId?: string
      status?: string
      inputs?: Array<{ inputId?: string; inputName?: string; quantityPerHa?: number; totalQuantity?: number; unit?: string }>
    }>) {
      const status = String(t.status || '').toLowerCase()
      if (status === 'done' || status === 'completed' || status === 'cancelled') continue
      const inputs = t.inputs || []
      if (inputs.length === 0) continue
      const plot = t.plotId ? plotById.get(t.plotId) : undefined
      const plotArea = Number(plot?.area) || 0
      const plotName = plot?.name || null

      for (let inputIndex = 0; inputIndex < inputs.length; inputIndex++) {
        const inp = inputs[inputIndex]
        if (!inp) continue
        const matches: Array<typeof inventoryItems[number]> = []
        if (inp.inputId) {
          const byInputId = inputIdToInvItems.get(inp.inputId)
          if (byInputId) matches.push(...byInputId)
        }
        if (matches.length === 0 && inp.inputName) {
          const byName = nameToInvItems.get(normalizeName(inp.inputName))
          if (byName) matches.push(...byName)
        }
        if (matches.length === 0) continue
        const row: NecessarioTaskRow = {
          taskId: t.id,
          title: t.title || '—',
          category: t.category || 'outros',
          plotName,
          plotArea,
          inputIndex,
          quantityPerHa: Number(inp.quantityPerHa) || 0,
          totalQuantity: Number(inp.totalQuantity) || 0,
          unit: inp.unit || '',
          status: t.status || 'pending',
          task: t as unknown as Task,
        }
        for (const match of matches) {
          const prev = map.get(match.id) || []
          prev.push(row)
          map.set(match.id, prev)
        }
      }
    }
    return map
  }, [allTasks, inventoryItems, activeLots])

  // AGC: Derived from the breakdown so the card number always matches the drill-down total.
  const necessarioByProductId = useMemo(() => {
    const map = new Map<string, number>()
    for (const [productId, rows] of necessarioBreakdownByProductId) {
      const total = rows.reduce((sum, r) => sum + (r.totalQuantity || 0), 0)
      if (total > 0) map.set(productId, total)
    }
    return map
  }, [necessarioBreakdownByProductId])

  // AGC: Breakdown sheet state — opened when user taps the red Necessário row on a tank/silo card.
  const [breakdownProductId, setBreakdownProductId] = useState<string | null>(null)

  // AGC: Inventory items that have at least one tank or silo assigned — by id AND by
  // AGC: normalised name. Name fallback handles orphan duplicate inventory docs (same
  // AGC: product name, different ids from historical creation) so they all hide together.
  // AGC: Universal rule (not fuel-specific): any item with a container → show only on the container card.
  const containerBacked = useMemo(() => {
    const ids = new Set<string>()
    const names = new Set<string>()
    for (const c of containers || []) {
      if (c.assignedProductId) {
        ids.add(c.assignedProductId)
        const item = inventoryItems.find(i => i.id === c.assignedProductId)
        if (item?.name) names.add(normalizeName(item.name))
      }
      if (c.assignedProductName) names.add(normalizeName(c.assignedProductName))
    }
    return { ids, names }
  }, [containers, inventoryItems])

  // AGC: Tasks with missing products – for quote builder "Add from tasks" (lot + task pairs with needToBuy > 0)
  const tasksWithMissing = useMemo<TaskQuoteOption[]>(() => {
    const options: TaskQuoteOption[] = []
    activeLots.forEach((lot: Plot) => {
      if (!lot.cropType || !lot.area) return
      const templates = taskTemplatesCache[lot.cropType] || []
      const doneTitles = completedTemplateTitlesByPlot.get(lot.id)
      templates.forEach((task: TaskTemplate) => {
        if (!task.inputs || task.inputs.length === 0) return
        // AGC: v3.10.4 — same completed-template filter as aggregatedInputs.
        if (doneTitles && doneTitles.has(normalizeName(task.title))) return
        const inputsWithNeed: { inputId: string; inputName: string; needToBuy: number; unit: string }[] = []
        const FUEL_CATS_QUOTE = new Set(['fuel', 'combustivel', 'combustível', 'diesel', 'gasolina', 'etanol'])
        task.inputs.forEach((input: TaskInput) => {
          // AGC: Skip fuel items — diesel purchased separately for tanks
          if (FUEL_CATS_QUOTE.has(String(input.category || '').toLowerCase())) return
          const totalQty = input.quantityPerHa * (lot.area || 0)
          const key = input.inputId || `${input.inputName}-${input.category}`
          const normalizedInputName = normalizeName(input.inputName)
          const matchCandidates = inventoryItems.filter(item => {
            if (item.inputId && input.inputId && item.inputId === input.inputId) return true
            const normalizedItemName = normalizeName(item.name)
            return normalizedItemName === normalizedInputName ||
              normalizedItemName.includes(normalizedInputName) ||
              normalizedInputName.includes(normalizedItemName)
          })
          const matchingItem = matchCandidates.length > 1
            ? matchCandidates.reduce((best, c) => ((c.quantity || 0) > (best.quantity || 0) ? c : best), matchCandidates[0])
            : matchCandidates[0]
          // AGC: v3.10.3 — same fuel-tank override as aggregatedInputs (diesel scope).
          const FUEL_CATS = new Set(['fuel', 'combustivel', 'combustível', 'diesel', 'gasolina', 'etanol'])
          const isFuel = FUEL_CATS.has(String(input.category || '').toLowerCase())
            || FUEL_CATS.has(String(matchingItem?.category || '').toLowerCase())
          let inStock = matchingItem?.quantity ?? 0
          if (isFuel && matchingItem) {
            const assignedTanks = (containers || []).filter(c => c.assignedProductId === matchingItem.id)
            if (assignedTanks.length > 0) {
              inStock = assignedTanks.reduce((s, c) => s + (c.currentLevel || 0), 0)
            }
          }
          const needToBuy = parseFloat(Math.max(0, totalQty - inStock).toFixed(2))
          if (needToBuy > 0) {
            inputsWithNeed.push({
              inputId: input.inputId || key,
              inputName: input.inputName,
              needToBuy,
              unit: input.unit,
            })
          }
        })
        if (inputsWithNeed.length > 0) {
          options.push({
            lotId: lot.id,
            lotName: lot.name || 'Sem nome',
            taskId: task.id,
            taskTitle: task.title,
            inputs: inputsWithNeed,
          })
        }
      })
    })
    return options
  }, [activeLots, inventoryItems, taskTemplatesCache, containers, completedTemplateTitlesByPlot])

  // AGC: All task options (including items with sufficient stock) for quote builder "show all" mode
  const allTaskOptions = useMemo<TaskQuoteOption[]>(() => {
    const options: TaskQuoteOption[] = []
    activeLots.forEach((lot: Plot) => {
      if (!lot.cropType || !lot.area) return
      const templates = taskTemplatesCache[lot.cropType] || []
      const doneTitles = completedTemplateTitlesByPlot.get(lot.id)
      templates.forEach((task: TaskTemplate) => {
        if (!task.inputs || task.inputs.length === 0) return
        if (doneTitles && doneTitles.has(normalizeName(task.title))) return
        const FUEL_CATS_ALL = new Set(['fuel', 'combustivel', 'combustível', 'diesel', 'gasolina', 'etanol'])
        const inputs = task.inputs
          .filter((input: TaskInput) => !FUEL_CATS_ALL.has(String(input.category || '').toLowerCase()))
          .map((input: TaskInput) => {
          const totalQty = input.quantityPerHa * (lot.area || 0)
          const key = input.inputId || `${input.inputName}-${input.category}`
          return {
            inputId: input.inputId || key,
            inputName: input.inputName,
            needToBuy: parseFloat(totalQty.toFixed(2)),
            unit: input.unit,
          }
        })
        if (inputs.length > 0) {
          options.push({
            lotId: lot.id,
            lotName: lot.name || 'Sem nome',
            taskId: task.id,
            taskTitle: task.title,
            inputs,
          })
        }
      })
    })
    return options
  }, [activeLots, taskTemplatesCache, completedTemplateTitlesByPlot])

  // AGC: Merge inventory and requirements into unified list
  const unifiedItems = useMemo(() => {
    const merged = mergeInventoryAndRequirements(inventoryItems, aggregatedInputs)
    
    // AGC: Sort by status (critical first), then by name
    return merged.sort((a, b) => {
      const statusOrder: Record<StockStatus, number> = {
        out: 0, critical: 1, low: 2, ok: 3, overstock: 4,
      }
      if (statusOrder[a.status] !== statusOrder[b.status]) {
        return statusOrder[a.status] - statusOrder[b.status]
      }
      return a.name.localeCompare(b.name)
    })
  }, [inventoryItems, aggregatedInputs, refreshCounter])
  
  // AGC: Convert to BOM items for modals
  const bomItems = useMemo(() => unifiedItems.map(toBOMItem), [unifiedItems])

  // AGC: All missing items for Quote builder tab "All missing stock" (same as Gerar pedido)
  const quoteAllMissingItems = useMemo(() => {
    return unifiedItems
      .filter(i => (i.needToBuy || 0) > 0)
      .map(i => ({
        inputId: i.inputId || i.id,
        name: i.name,
        unit: i.unit,
        needToBuy: i.needToBuy || 0,
      }))
  }, [unifiedItems])

  // AGC: All items for Quote builder tab "All items" (searchable)
  const quoteAllItems = useMemo(() => {
    const seen = new Set<string>()
    return unifiedItems
      .filter(i => {
        const key = i.inputId || i.id || i.name
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
      .map(i => ({ inputId: i.inputId || i.id, name: i.name, unit: i.unit }))
  }, [unifiedItems])
  
  // AGC: Filter by search, category, and source
  // AGC: Base filtering (search, category, source) — used for dashboard badge counts
  const baseFilteredItems = useMemo(() => {
    return unifiedItems.filter(item => {
      const matchesSearch = searchTerm === '' ||
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory
      const matchesSource = selectedSource === 'all' ||
        item.source === selectedSource ||
        (selectedSource === 'requirement' && item.source === 'both') ||
        (selectedSource === 'inventory' && item.source === 'both')
      return matchesSearch && matchesCategory && matchesSource
    })
  }, [unifiedItems, searchTerm, selectedCategory, selectedSource])

  // AGC: Final filtering adds status badge filter + inStockOnly
  const filteredItems = useMemo(() => {
    return baseFilteredItems.filter(item => {
      const matchesStatus = statusFilter === null || (
        statusFilter === 'ok' ? (item.status === 'ok' || item.status === 'overstock') :
        statusFilter === 'low' ? item.status === 'low' :
        statusFilter === 'critical' ? (item.status === 'critical' || item.status === 'out') :
        true
      )
      const matchesInStock = !inStockOnly || item.inStock > 0
      return matchesStatus && matchesInStock
    })
  }, [baseFilteredItems, statusFilter, inStockOnly])
  
  // AGC: Group items by category for accordion view
  const itemsByCategory = useMemo(() => {
    const grouped = new Map<InputCategory, CategoryInputItem[]>()

    filteredItems.forEach(item => {
      // AGC: Hide items that live in a tank/silo — their data is rendered on the container card.
      // AGC: Check by id first (direct link), then by normalised name to catch orphan duplicate
      // AGC: inventory docs that share a name with a tank-backed item.
      const linkedInvId = item.inventoryItemId || item.inputId || item.id
      if (linkedInvId && containerBacked.ids.has(linkedInvId)) return
      if (item.name && containerBacked.names.has(normalizeName(item.name))) return

      const items = grouped.get(item.category) || []
      const pendingOrder = pendingOrdersMap.get(item.inputId || item.id)
      
      items.push({
        inputId: item.inputId || item.id,
        name: item.name,
        category: item.category,
        unit: item.unit,
        totalRequired: item.totalRequired || 0,
        inStock: item.inStock,
        needToBuy: item.needToBuy || 0,
        status: item.status,
        tasks: item.tasks || [],
        lotsNeeding: item.lotsNeeding || [],
        hasPendingOrder: !!pendingOrder,
        pendingOrderQty: pendingOrder?.totalOrdered,
        // AGC: Extended properties for unified view
        source: item.source,
        inventoryItemId: item.inventoryItemId,
      } as CategoryInputItem & { source?: ItemSource; inventoryItemId?: string })
      grouped.set(item.category, items)
    })
    
    // AGC: Sort categories by worst status
    const sortedCategories = Array.from(grouped.entries()).sort(([catA, itemsA], [catB, itemsB]) => {
      const getWorstStatus = (items: CategoryInputItem[]) => {
        if (items.some(i => i.status === 'out' || i.status === 'critical')) return 0
        if (items.some(i => i.status === 'low')) return 1
        return 2
      }
      return getWorstStatus(itemsA) - getWorstStatus(itemsB)
    })
    
    return sortedCategories
  }, [filteredItems, pendingOrdersMap, containerBacked])
  
  // AGC: Dashboard badge counts — from baseFilteredItems so they stay stable when a badge is active
  const summary = useMemo(() => ({
    totalItems: baseFilteredItems.length,
    inStock: baseFilteredItems.filter(i => i.status === 'ok' || i.status === 'overstock').length,
    low: baseFilteredItems.filter(i => i.status === 'low').length,
    critical: baseFilteredItems.filter(i => i.status === 'critical' || i.status === 'out').length,
    inventoryOnly: baseFilteredItems.filter(i => i.source === 'inventory').length,
    requirementOnly: baseFilteredItems.filter(i => i.source === 'requirement').length,
    both: baseFilteredItems.filter(i => i.source === 'both').length,
  }), [baseFilteredItems])
  
  // AGC: Handle refresh (button-triggered — pull-to-refresh handles its own state).
  // AGC: Refetch inventory + plots + templates + tasks in parallel. Templates are
  // AGC: force-refetched (keeping the old cache visible until the new data arrives)
  // AGC: so the Necessário row on tank/silo cards doesn't flicker off during refresh.
  const handleRefresh = async () => {
    await Promise.all([
      refreshInventory(),
      refreshPlots(),
      fetchAllTemplates(true),
      refetchTasks(),
    ])
    setRefreshCounter(prev => prev + 1)
  }
  
  // AGC: Handle expand/collapse all
  const handleExpandAll = () => {
    const allCategories = new Set(itemsByCategory.map(([cat]) => cat))
    setExpandedCategories(allCategories)
  }
  
  const handleCollapseAll = () => {
    setExpandedCategories(new Set())
  }
  
  // AGC: Toggle category expansion
  const toggleCategory = (category: InputCategory) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev)
      if (newSet.has(category)) {
        newSet.delete(category)
      } else {
        newSet.add(category)
      }
      return newSet
    })
  }
  
  // AGC: Handle item click - open supply detail modal
  const handleItemClick = (item: CategoryInputItem) => {
    // AGC: Find the unified item
    const unifiedItem = unifiedItems.find(u => 
      (u.inputId && u.inputId === item.inputId) || 
      u.id === item.inputId ||
      normalizeName(u.name) === normalizeName(item.name)
    )
    
    if (unifiedItem) {
      setSelectedSupplyItem(unifiedItem)
      setIsSupplyDetailOpen(true)
    }
  }
  
  // AGC: Handle stock entry
  const handleStockEntry = async (entries: StockEntryItem[]) => {
    let successCount = 0
    let createdCount = 0
    
    try {
      for (const entry of entries) {
        if (entry.quantityToAdd > 0) {
          let existingItem = inventoryItems.find(i => 
            i.name.toLowerCase() === entry.inputName.toLowerCase() ||
            i.inputId === entry.inputId
          )
          
          if (!existingItem && addItem) {
            try {
              const newItem = await addItem({
                name: entry.inputName,
                inputId: entry.inputId,
                category: entry.category || 'other',
                quantity: 0,
                unit: entry.unit,
                minimumStock: 0,
              })
              existingItem = newItem
              createdCount++
            } catch (createError) {
              console.error(`AGC: Failed to create inventory item "${entry.inputName}":`, createError)
              continue
            }
          }
          
          if (existingItem && recordMovement) {
            await recordMovement(existingItem.id, {
              type: 'entry',
              quantity: entry.quantityToAdd,
              reason: t('inventory.stockEntry') || 'Entrada de estoque',
            })
            successCount++
          }
        }
      }
      
      await handleRefresh()
      
      if (successCount > 0) {
        if (createdCount > 0) {
          toast.success(`${successCount} item(s) ${t('inventory.addedToStock')}. ${createdCount} ${t('inventory.newItemsCreated')}.`)
        } else {
          toast.success(t('inventory.entrySuccess') || 'Estoque atualizado!')
        }
      } else {
        toast.error(t('inventory.noItemsAdded') || 'Nenhum item foi adicionado')
      }
    } catch (error) {
      console.error('AGC: Error adding stock entries:', error)
      toast.error(t('inventory.entryError') || 'Erro ao adicionar estoque')
      throw error
    }
  }

  // AGC: Register new product (create inventory item without requiring stock or BOM)
  const handleRegisterProduct = async () => {
    const name = (registerProductForm.name || '').trim()
    if (!name) {
      toast.error(t('inventory.registerProduct.nameRequired') || 'Informe o nome do produto')
      return
    }
    if (!addItem) return
    setIsRegisterProductSubmitting(true)
    try {
      // AGC: Create item with quantity 0 — the recordMovement call below handles the actual stock entry
      // AGC: Previously this set quantity to initialQuantity AND then recordMovement added it again, doubling the total
      const newItem = await addItem({
        name,
        category: registerProductForm.category as InputCategory,
        unit: registerProductForm.unit,
        quantity: 0,
        minimumStock: registerProductForm.minimumStock,
        costPerUnit: registerProductForm.costPerUnit || 0,
      })
      if (registerProductForm.initialQuantity > 0 && recordMovement) {
        // AGC: Include costPerUnit so the movement appears in price history
        const movementPayload: { type: 'entry'; quantity: number; reason: string; costPerUnit?: number; totalCost?: number } = {
          type: 'entry',
          quantity: registerProductForm.initialQuantity,
          reason: t('inventory.stockEntry') || 'Entrada de estoque',
        }
        if (registerProductForm.costPerUnit > 0) {
          movementPayload.costPerUnit = registerProductForm.costPerUnit
          movementPayload.totalCost = registerProductForm.costPerUnit * registerProductForm.initialQuantity
        }
        await recordMovement(newItem.id, movementPayload)
      }
      toast.success(t('inventory.registerProduct.success') || 'Produto cadastrado!')
      setIsRegisterProductOpen(false)
      setRegisterProductForm({ name: '', category: 'other', unit: 'L', initialQuantity: 0, minimumStock: 0, costPerUnit: 0 })
      await handleRefresh()
    } catch (error) {
      console.error('AGC: Error registering product:', error)
      toast.error(t('inventory.registerProduct.error') || 'Erro ao cadastrar produto')
    } finally {
      setIsRegisterProductSubmitting(false)
    }
  }

  // AGC: Get translations for PDF
  const getQuoteTranslations = (): QuoteTranslations => ({
    quoteRequest: t('lotPage.quoteRequest'),
    quoteSubtitle: t('lotPage.quoteSubtitle'),
    farmLabel: t('lotPage.farmLabel'),
    locationLabel: t('lotPage.locationLabel'),
    dateLabel: t('lotPage.dateLabel'),
    generatedBy: t('lotPage.generatedBy'),
    itemColumn: t('lotPage.itemColumn'),
    qtyColumn: t('lotPage.qtyColumn'),
    unitColumn: t('lotPage.unitColumn'),
    supplierInfo: t('inputs.orders.supplier.title'),
  })
  
  const isLoading = loadingInventory || loadingLots || loadingTemplates

  // AGC: Guard against infinite loading — after 10s, stop showing spinner so user sees content or empty state.
  const [loadingTimedOut, setLoadingTimedOut] = useState(false)
  useEffect(() => {
    if (!isLoading) { setLoadingTimedOut(false); return }
    const timer = setTimeout(() => setLoadingTimedOut(true), 10000)
    return () => clearTimeout(timer)
  }, [isLoading])
  const effectiveLoading = isLoading && !loadingTimedOut

  // AGC: Count items by status for share menu
  return (
    <div
      ref={scrollRef}
      className="min-h-screen bg-[#F4F5F7] dark:bg-slate-900 pb-48 overflow-y-auto"
      {...touchHandlers}
    >
      {/* AGC: Pull-to-refresh indicator */}
      {(pullDistance > 0 || isRefreshing) && (
        <div
          className="flex items-center justify-center gap-2 overflow-hidden transition-all duration-200 bg-[#F4F5F7] dark:bg-slate-900"
          style={{ height: isRefreshing ? pullThreshold : pullDistance }}
        >
          <svg className={`w-4 h-4 text-green-600 dark:text-green-400 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {isRefreshing ? t('common.syncing') : pullDistance >= pullThreshold ? t('common.releaseToRefresh') : t('common.pullToRefresh')}
          </span>
        </div>
      )}

      {/* AGC: Top Bar */}
      <TopBar
        title={t('inventory.title') || 'Inventário'}
        subtitle={t('inventory.subtitle') || 'Gestão de insumos e estoque'}
        onBack={onBack}
        actions={
          <div className="flex items-center gap-1">
            {/* AGC: View toggle */}
            <button
              onClick={() => setViewMode(viewMode === 'accordion' ? 'list' : 'accordion')}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
              title={viewMode === 'accordion' ? 'Lista' : 'Categorias'}
            >
              {viewMode === 'accordion' 
                ? <LayoutGrid className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                : <ListOrdered className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              }
            </button>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
            >
              <RefreshCw className={`w-5 h-5 text-gray-600 dark:text-gray-400 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        }
      />
      
      {/* AGC: Summary Cards - Compact, clickable to filter by status */}
      <div className="px-4 pt-4">
        <div className="grid grid-cols-4 gap-2">
          <button
            onClick={() => setStatusFilter(null)}
            className={`rounded-xl p-2 shadow-sm border transition-all ${
              statusFilter === null
                ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-600 ring-1 ring-blue-300'
                : 'bg-white dark:bg-slate-800 border-gray-100 dark:border-slate-700'
            }`}
          >
            <div className="flex flex-col items-center">
              <Package className="w-4 h-4 text-blue-500 mb-0.5" />
              <span className="text-lg font-bold text-gray-900 dark:text-white">{summary.totalItems}</span>
              <span className="text-[9px] text-gray-500 dark:text-gray-400">Total</span>
            </div>
          </button>

          <button
            onClick={() => setStatusFilter(statusFilter === 'ok' ? null : 'ok')}
            className={`rounded-xl p-2 shadow-sm border transition-all ${
              statusFilter === 'ok'
                ? 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-300 dark:border-emerald-600 ring-1 ring-emerald-300'
                : 'bg-white dark:bg-slate-800 border-gray-100 dark:border-slate-700'
            }`}
          >
            <div className="flex flex-col items-center">
              <Check className="w-4 h-4 text-emerald-500 mb-0.5" />
              <span className="text-lg font-bold text-gray-900 dark:text-white">{summary.inStock}</span>
              <span className="text-[9px] text-gray-500 dark:text-gray-400">OK</span>
            </div>
          </button>

          <button
            onClick={() => setStatusFilter(statusFilter === 'low' ? null : 'low')}
            className={`rounded-xl p-2 shadow-sm border transition-all ${
              statusFilter === 'low'
                ? 'bg-amber-50 dark:bg-amber-900/30 border-amber-300 dark:border-amber-600 ring-1 ring-amber-300'
                : 'bg-white dark:bg-slate-800 border-gray-100 dark:border-slate-700'
            }`}
          >
            <div className="flex flex-col items-center">
              <AlertTriangle className="w-4 h-4 text-amber-500 mb-0.5" />
              <span className="text-lg font-bold text-gray-900 dark:text-white">{summary.low}</span>
              <span className="text-[9px] text-gray-500 dark:text-gray-400">{t('inventory.stockStatus.low') || 'Baixo'}</span>
            </div>
          </button>

          <button
            onClick={() => setStatusFilter(statusFilter === 'critical' ? null : 'critical')}
            className={`rounded-xl p-2 shadow-sm border transition-all ${
              statusFilter === 'critical'
                ? 'bg-red-50 dark:bg-red-900/30 border-red-300 dark:border-red-600 ring-1 ring-red-300'
                : 'bg-white dark:bg-slate-800 border-gray-100 dark:border-slate-700'
            }`}
          >
            <div className="flex flex-col items-center">
              <ShoppingCart className="w-4 h-4 text-red-500 mb-0.5" />
              <span className="text-lg font-bold text-gray-900 dark:text-white">{summary.critical}</span>
              <span className="text-[9px] text-gray-500 dark:text-gray-400">{t('inventory.needToBuy') || 'Comprar'}</span>
            </div>
          </button>
        </div>
      </div>
      
      {/* AGC: Toolbar Row - Compact inline buttons */}
      <div className="px-4 pt-3">
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
          {/* AGC: Warehouse selector — visible only when 2+ warehouses */}
          {hasMultipleWarehouses && (
            <WarehouseSelector
              warehouses={warehouses}
              selected={selectedWarehouseFilter}
              onChange={setWarehouseFilter}
              onManage={() => setIsManageWarehousesOpen(true)}
            />
          )}

          {/* AGC: Transfer stock button (visible with multiple warehouses) */}
          {hasMultipleWarehouses && (
            <button
              onClick={() => setIsTransferStockOpen(true)}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
            >
              ⇄ Transfer
            </button>
          )}

          {/* AGC: Generate Order Button */}
          <button
            onClick={() => setIsOrderBuilderOpen(true)}
            disabled={summary.critical === 0}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
              summary.critical === 0
                ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
                : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm'
            }`}
          >
            <ShoppingCart className="w-3.5 h-3.5" />
            <span>{t('inventory.generateOrder') || 'Gerar Pedido'}</span>
            {summary.critical > 0 && (
              <span className="px-1.5 py-0.5 bg-white/20 rounded-full text-[10px]">
                {summary.critical}
              </span>
            )}
          </button>
          
          {/* AGC: Solicitar cotação – opens Quote Builder (empty + add from tasks + supplier + WhatsApp) */}
          <button
            onClick={() => setIsQuoteBuilderOpen(true)}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
          >
            <Share2 className="w-3.5 h-3.5" />
            <span>{t('inventory.shareQuote') || 'Cotação'}</span>
          </button>

          {/* AGC: Pedidos page — counter = unique open (non-received/non-cancelled) orders */}
          <button
            onClick={() => onNavigate?.('purchase-orders')}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
          >
            <Package className="w-3.5 h-3.5" />
            <span>{t('inputs.orders.pageTitle') || 'Pedidos'}</span>
            {openOrdersCount > 0 && (
              <span className="px-1.5 py-0.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-full text-[10px] font-semibold">
                {openOrdersCount}
              </span>
            )}
          </button>
        </div>
      </div>
      
      {/* AGC: Search and Expand/Collapse */}
      <div className="px-4 pt-3">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('inventory.searchPlaceholder') || 'Buscar insumos...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300)}
              className="w-full pl-9 pr-4 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 text-sm focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
            />
          </div>
          
          {viewMode === 'accordion' && (
            <>
              <button
                onClick={handleExpandAll}
                className="px-3 py-2 rounded-xl border bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-400 text-xs"
                title={t('inventory.expandAll') || 'Expandir tudo'}
              >
                <ChevronDown className="w-4 h-4" />
              </button>
              <button
                onClick={handleCollapseAll}
                className="px-3 py-2 rounded-xl border bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-400 text-xs"
                title={t('inventory.collapseAll') || 'Recolher tudo'}
              >
                <ChevronUp className="w-4 h-4" />
              </button>
            </>
          )}
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-3 py-2 rounded-xl border flex items-center gap-1 transition-colors text-xs ${
              showFilters 
                ? 'bg-emerald-50 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-700 text-emerald-700 dark:text-emerald-400' 
                : 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-400'
            }`}
          >
            <Filter className="w-4 h-4" />
          </button>
        </div>
        
        {showFilters && (
          <div className="mt-3 space-y-3">
            {/* AGC: Source filter - NEW */}
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                {t('inventory.filterBySource') || 'Origem'}
              </label>
              <div className="flex gap-2">
                {(['all', 'inventory', 'requirement'] as SourceFilter[]).map((source) => (
                  <button
                    key={source}
                    onClick={() => setSelectedSource(source)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      selectedSource === source
                        ? 'bg-emerald-600 text-white'
                        : 'bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    {source === 'all' ? (
                      <>
                        <Layers className="w-3.5 h-3.5" />
                        {t('common.all') || 'Todos'}
                      </>
                    ) : source === 'inventory' ? (
                      <>
                        <Database className="w-3.5 h-3.5" />
                        {t('inventory.source.inventory') || 'Inventário'}
                      </>
                    ) : (
                      <>
                        <ClipboardList className="w-3.5 h-3.5" />
                        {t('inventory.source.requirement') || 'Requeridos'}
                      </>
                    )}
                  </button>
                ))}
              </div>
            </div>
            
            {/* AGC: Lot filter */}
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                {t('inventory.filterByLot') || 'Lote'}
              </label>
              <div className="relative">
                <select
                  value={selectedLotId}
                  onChange={(e) => setSelectedLotId(e.target.value)}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-gray-900 dark:text-white text-sm appearance-none pr-10"
                >
                  <option value="all">{t('inventory.allLots') || 'Todos os lotes'}</option>
                  {activeLots.map(lot => (
                    <option key={lot.id} value={lot.id}>
                      {lot.name || 'Sem nome'} ({lot.area?.toFixed(1)} ha)
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
            
            {/* AGC: Category filter */}
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                {t('inventory.category') || 'Categoria'}
              </label>
              <div className="relative">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-gray-900 dark:text-white text-sm appearance-none pr-10"
                >
                  <option value="all">{t('common.allCategories') || 'Todas as categorias'}</option>
                  {INPUT_CATEGORIES.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.namePt}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* AGC: Silo / Tank Section — above product list, below search */}
      <div className="px-4 pt-3">
        <SiloTankSection
          containers={containers}
          warehouses={warehouses}
          inventoryItems={inventoryItems}
          defaultWarehouseId={warehouses[0]?.id}
          warehouseHook={warehouseHook}
          warehouseFilter={selectedWarehouseFilter}
          necessarioByProductId={necessarioByProductId}
          previsaoByProductId={previsaoByProductId}
          onNecessarioClick={setBreakdownProductId}
        />
        {/* AGC: "See in stock only" toggle — flat table of items with quantity > 0 */}
        <button
          onClick={() => setInStockOnly(prev => !prev)}
          className={`mt-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
            inStockOnly
              ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700'
              : 'text-gray-400 dark:text-gray-500 border border-gray-200/60 dark:border-gray-700/40 hover:text-gray-600 hover:border-gray-300'
          }`}
        >
          {t('inventory.seeInStockOnly') || 'Ver apenas em estoque'}
          {inStockOnly && ' ✕'}
        </button>
      </div>

      {/* AGC: Items List - Accordion View */}
      <div className="px-4 pt-4 space-y-2">
        {effectiveLoading ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400 text-sm">{t('common.loading') || 'Carregando...'}</p>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">{t('inventory.noItems') || 'Nenhum item encontrado'}</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
              {t('inventory.addItemsHint') || 'Adicione itens ao inventário ou configure tareas com insumos'}
            </p>
          </div>
        ) : (viewMode === 'accordion' && !inStockOnly) ? (
          // AGC: Accordion view - Categories with collapsible items (disabled when inStockOnly)
          itemsByCategory.map(([category, items]) => (
            <CategoryAccordion
              key={category}
              category={category}
              items={items}
              isExpanded={expandedCategories.has(category)}
              onToggle={() => toggleCategory(category)}
              onItemClick={handleItemClick}
              onAddToOrder={(item) => {
                setIsOrderBuilderOpen(true)
              }}
              onViewOrder={() => onNavigate?.('purchase-orders')}
            />
          ))
        ) : (
          // AGC: Flat list view - All items in a single scrollable list
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            {/* AGC: Table Header — aligned with body columns */}
            <div className="grid grid-cols-12 gap-1 px-3 py-2 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700 text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              <div className="col-span-1"></div>
              <div className="col-span-4">{t('inventory.inputName') || 'Insumo'}</div>
              <div className="col-span-2 text-right">{t('inventory.required') || 'Necessário'}</div>
              <div className="col-span-2 text-right">{t('inventory.estoque') || 'Estoque'}</div>
              <div className="col-span-3 text-right">{t('inventory.status') || 'Status'}</div>
            </div>
            
            {/* AGC: Table Body */}
            {filteredItems.map((item) => {
              const catInfo = getInputCategoryInfo(item.category)
              const CategoryIcon = catInfo?.iconComponent
              const sourceInfo = getSourceInfo(item.source)
              const statusInfo = getStatusInfo(item.status)
              
              return (
                <div 
                  key={item.id}
                  onClick={() => {
                    setSelectedSupplyItem(item)
                    setIsSupplyDetailOpen(true)
                  }}
                  className={`grid grid-cols-12 gap-1 px-3 py-2 items-center text-xs border-b border-gray-100 dark:border-gray-700/50 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer ${
                    item.status === 'out' || item.status === 'critical' 
                      ? 'bg-red-50/50 dark:bg-red-900/10' 
                      : item.status === 'low' 
                      ? 'bg-amber-50/50 dark:bg-amber-900/10'
                      : ''
                  }`}
                >
                  {/* AGC: Category Icon */}
                  <div className="col-span-1">
                    {CategoryIcon && (
                      <CategoryIcon
                        className="w-4 h-4"
                        style={{ color: catInfo?.color }}
                      />
                    )}
                  </div>

                  {/* AGC: Name + Source Badge */}
                  <div className="col-span-4 min-w-0">
                    <div className="truncate text-gray-900 dark:text-white font-medium">
                      {item.name}
                    </div>
                    <span className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[8px] font-medium ${sourceInfo.bgColor} ${sourceInfo.color}`}>
                      {sourceInfo.icon}
                      {sourceInfo.label}
                    </span>
                  </div>

                  {/* AGC: Required (smart rounding) */}
                  <div className="col-span-2 text-right text-gray-600 dark:text-gray-400">
                    {item.totalRequired ? formatQty(item.totalRequired) : '-'} <span className="text-[9px]">{item.unit}</span>
                  </div>

                  {/* AGC: Estoque */}
                  <div className={`col-span-2 text-right font-medium ${item.inStock === 0 ? 'text-red-600' : 'text-gray-900 dark:text-white'}`}>
                    {formatQty(item.inStock)} <span className="text-[9px] font-normal text-gray-500">{item.unit}</span>
                  </div>

                  {/* AGC: Status pill (fixed position) + always-visible "+" button */}
                  <div className="col-span-3 flex items-center justify-end gap-1.5">
                    <div className="flex-1 flex justify-end pr-1">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-semibold ${statusInfo.bgColor} ${statusInfo.color}`}>
                        {statusInfo.label}
                      </span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setSelectedSupplyItem(item)
                        setIsSupplyDetailOpen(true)
                      }}
                      className="flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-full bg-emerald-500 text-white hover:bg-emerald-600 active:scale-95 transition-all shadow-sm"
                      aria-label={t('inputs.addToOrder') || 'Adicionar ao pedido'}
                    >
                      <Plus className="w-4 h-4" strokeWidth={3} />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
      
      {/* AGC: FAB – overlay to close menu when tapping outside */}
      {isFabMenuOpen && (
        <button
          type="button"
          aria-label="Close menu"
          className="fixed inset-0 z-[90] bg-transparent"
          onClick={() => setIsFabMenuOpen(false)}
        />
      )}
      {/* AGC: FAB – only if canManageInventory; menu expands upward above + */}
      <div
        className={`fixed right-4 z-[90] flex flex-col-reverse items-end ${!canManageInventory ? 'hidden' : ''}`}
        style={{ bottom: 'calc(env(safe-area-inset-bottom) + 6rem)' }}
      >
        {/* AGC: Main + button */}
        <button
          type="button"
          onClick={() => setIsFabMenuOpen(prev => !prev)}
          className="w-14 h-14 rounded-full text-white shadow-lg flex items-center justify-center transition-all duration-200 hover:opacity-90 hover:shadow-xl active:scale-95 focus:outline-none focus:ring-2 focus:ring-offset-2 shrink-0 z-10"
          style={{ backgroundColor: 'var(--accent-primary)' }}
          aria-label={t('inventory.addItem') || 'Adicionar'}
        >
          <Plus className={`w-6 h-6 transition-transform duration-200 ${isFabMenuOpen ? 'rotate-45' : ''}`} />
        </button>
        {/* AGC: Menu column above +: 1st Add product, 2nd Add stock, 3rd Take picture; pop one by one via global CSS animation */}
        {isFabMenuOpen && (
          <div className="flex flex-col-reverse gap-5 mb-3">
            {/* AGC: 1st pop – Add product (nearest to +) */}
            <button
              type="button"
              onClick={() => { setIsFabMenuOpen(false); setIsRegisterProductOpen(true) }}
              className="w-[4.5rem] h-[4.5rem] rounded-full bg-white dark:bg-slate-700 border-2 border-gray-200 dark:border-slate-600 shadow-lg flex items-center justify-center text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 animate-fab-pop-in"
              style={{ animationDelay: '0ms' }}
              aria-label={t('inventory.registerProduct.button')}
            >
              <Plus className="w-8 h-8" />
            </button>
            {/* AGC: 2nd pop – Add stock */}
            <button
              type="button"
              onClick={() => { setIsFabMenuOpen(false); setIsAddStockModalOpen(true) }}
              className="w-[4.5rem] h-[4.5rem] rounded-full bg-white dark:bg-slate-700 border-2 border-gray-200 dark:border-slate-600 shadow-lg flex items-center justify-center text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 animate-fab-pop-in"
              style={{ animationDelay: '120ms' }}
              aria-label={t('inventory.addStock')}
            >
              <ArrowUpCircle className="w-8 h-8" />
            </button>
            {/* AGC: 3rd pop – Take picture */}
            <button
              type="button"
              onClick={() => { setIsFabMenuOpen(false); setIsScanInvoiceModalOpen(true) }}
              className="w-[4.5rem] h-[4.5rem] rounded-full bg-white dark:bg-slate-700 border-2 border-gray-200 dark:border-slate-600 shadow-lg flex items-center justify-center text-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/30 animate-fab-pop-in"
              style={{ animationDelay: '240ms' }}
              aria-label={t('inventory.scanInvoice')}
            >
              <Camera className="w-8 h-8" />
            </button>
          </div>
        )}
      </div>
      
      {/* AGC: BottomNav is rendered by MainMenu — no page should render its own */}

      {/* AGC: Add Stock Modal */}
      <AddStockModal
        isOpen={isAddStockModalOpen}
        onClose={() => setIsAddStockModalOpen(false)}
        currentLotId="all"
        currentLotName={t('inventory.allLots') || 'Todos os Lotes'}
        bomItems={bomItems}
        allLots={activeLots.map(l => ({ id: l.id, name: l.name || 'Sem nome', cropName: l.cropName || l.cropType || '' }))}
        onConfirmEntry={handleStockEntry}
      />

      {/* AGC: Register new product modal – same pattern as Add Stock (bottom sheet on mobile, centered on desktop) */}
      {isRegisterProductOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[90] flex items-end sm:items-center justify-center"
          onClick={() => !isRegisterProductSubmitting && setIsRegisterProductOpen(false)}
        >
          <div
            className="bg-white dark:bg-slate-800 w-full sm:w-[500px] sm:max-h-[85vh] max-h-[90vh] rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* AGC: Header – same pattern as Add Stock */}
            <div className="sticky top-0 bg-white dark:bg-slate-800 border-b border-gray-100 dark:border-slate-700 px-4 py-4 z-10 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                    <Plus className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-gray-900 dark:text-white">
                      {t('inventory.registerProduct.title') || 'Cadastrar novo produto'}
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('inventory.registerProduct.subtitle') || 'Adicionar novo item ao inventário'}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => !isRegisterProductSubmitting && setIsRegisterProductOpen(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            {/* AGC: Form – category full-width, unit full-width below, qty + min stock side-by-side */}
            <div className="p-4 space-y-4 overflow-auto flex-1 min-h-0">
              {/* AGC: Product name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('inventory.itemName') || 'Nome do item'} *
                </label>
                <input
                  type="text"
                  value={registerProductForm.name}
                  onChange={(e) => setRegisterProductForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder={t('inventory.registerProduct.namePlaceholder') || 'Ex: Glifosato, Ureia...'}
                  className="w-full px-3 py-2.5 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 text-sm"
                />
              </div>

              {/* AGC: Category – full width row */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('inventory.category') || 'Categoria'}
                </label>
                <div className="flex gap-1" ref={registerCategoryDropdownRef}>
                  <div className="flex-1 min-w-0 relative">
                    <button
                      type="button"
                      onClick={() => { setRegisterCategoryDropdownOpen((v) => !v); setRegisterUnitDropdownOpen(false) }}
                      className="w-full px-3 py-2.5 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl text-gray-900 dark:text-white text-sm text-left flex items-center justify-between gap-2"
                    >
                      <span className="truncate">
                        {[...builtInCategoriesForModal, ...customCategories].find((c) => c.id === registerProductForm.category)?.label ?? registerProductForm.category}
                      </span>
                      <ChevronDown className={`w-4 h-4 shrink-0 text-gray-400 transition-transform ${registerCategoryDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {registerCategoryDropdownOpen && (
                      <div className="absolute z-10 mt-1 w-full bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl shadow-lg max-h-[10rem] overflow-y-auto">
                        {builtInCategoriesForModal.map((cat) => (
                          <button
                            key={cat.id}
                            type="button"
                            onClick={() => { setRegisterProductForm((p) => ({ ...p, category: cat.id })); setRegisterCategoryDropdownOpen(false) }}
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-slate-600 ${registerProductForm.category === cat.id ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300' : 'text-gray-900 dark:text-white'}`}
                          >
                            {cat.label}
                          </button>
                        ))}
                        {customCategories.map((cat) => (
                          <button
                            key={cat.id}
                            type="button"
                            onClick={() => { setRegisterProductForm((p) => ({ ...p, category: cat.id })); setRegisterCategoryDropdownOpen(false) }}
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-slate-600 ${registerProductForm.category === cat.id ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300' : 'text-gray-900 dark:text-white'}`}
                          >
                            {cat.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setManageCategoriesUnitsMode('categories')}
                    className="shrink-0 w-10 h-10 rounded-xl border border-gray-200 dark:border-slate-600 flex items-center justify-center text-gray-500 hover:bg-gray-50 dark:hover:bg-slate-700"
                    aria-label={t('inventory.manageCategoriesUnits.manageCategories') || 'Manage categories'}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* AGC: Unit – full width row */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('inventory.unit') || 'Unidade'}
                </label>
                <div className="flex gap-1" ref={registerUnitDropdownRef}>
                  <div className="flex-1 min-w-0 relative">
                    <button
                      type="button"
                      onClick={() => { setRegisterUnitDropdownOpen((v) => !v); setRegisterCategoryDropdownOpen(false) }}
                      className="w-full px-3 py-2.5 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl text-gray-900 dark:text-white text-sm text-left flex items-center justify-between gap-2"
                    >
                      <span className="truncate">
                        {[...BUILT_IN_UNITS, ...customUnits].find((u) => u.id === registerProductForm.unit)?.label ?? registerProductForm.unit}
                      </span>
                      <ChevronDown className={`w-4 h-4 shrink-0 text-gray-400 transition-transform ${registerUnitDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {registerUnitDropdownOpen && (
                      <div className="absolute z-10 mt-1 w-full bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl shadow-lg max-h-[10rem] overflow-y-auto">
                        {BUILT_IN_UNITS.map((u) => (
                          <button
                            key={u.id}
                            type="button"
                            onClick={() => { setRegisterProductForm((p) => ({ ...p, unit: u.id })); setRegisterUnitDropdownOpen(false) }}
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-slate-600 ${registerProductForm.unit === u.id ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300' : 'text-gray-900 dark:text-white'}`}
                          >
                            {u.label}
                          </button>
                        ))}
                        {customUnits.map((u) => (
                          <button
                            key={u.id}
                            type="button"
                            onClick={() => { setRegisterProductForm((p) => ({ ...p, unit: u.id })); setRegisterUnitDropdownOpen(false) }}
                            className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-slate-600 ${registerProductForm.unit === u.id ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300' : 'text-gray-900 dark:text-white'}`}
                          >
                            {u.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setManageCategoriesUnitsMode('units')}
                    className="shrink-0 w-10 h-10 rounded-xl border border-gray-200 dark:border-slate-600 flex items-center justify-center text-gray-500 hover:bg-gray-50 dark:hover:bg-slate-700"
                    aria-label={t('inventory.manageCategoriesUnits.manageUnits') || 'Manage units'}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* AGC: Price per unit */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('inventory.registerProduct.costPerUnit') || 'Preço por unidade'}
                </label>
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  value={registerProductForm.costPerUnit || ''}
                  onChange={(e) => setRegisterProductForm((prev) => ({ ...prev, costPerUnit: clampDecimals(parseFloat(e.target.value) || 0) }))}
                  placeholder="R$ 0,00"
                  className="w-full px-3 py-2.5 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl text-gray-900 dark:text-white text-sm"
                />
              </div>

              {/* AGC: Initial quantity + minimum stock side by side */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('inventory.registerProduct.initialQuantity') || 'Quantidade inicial'}
                  </label>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={registerProductForm.initialQuantity || ''}
                    onChange={(e) => setRegisterProductForm((prev) => ({ ...prev, initialQuantity: clampDecimals(parseFloat(e.target.value) || 0) }))}
                    className="w-full px-3 py-2.5 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl text-gray-900 dark:text-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('inventory.minStock') || 'Estoque mínimo'}
                  </label>
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={registerProductForm.minimumStock || ''}
                    onChange={(e) => setRegisterProductForm((prev) => ({ ...prev, minimumStock: clampDecimals(parseFloat(e.target.value) || 0) }))}
                    className="w-full px-3 py-2.5 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-xl text-gray-900 dark:text-white text-sm"
                  />
                </div>
              </div>
            </div>

            {/* AGC: Footer – same pattern as Add Stock */}
            <div className="sticky bottom-0 bg-white dark:bg-slate-800 border-t border-gray-100 dark:border-slate-700 px-4 py-4 z-10 flex-shrink-0 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setIsRegisterProductOpen(false)}
                disabled={isRegisterProductSubmitting}
                className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 text-sm font-medium disabled:opacity-50"
              >
                {t('common.cancel') || 'Cancelar'}
              </button>
              <button
                type="button"
                onClick={handleRegisterProduct}
                disabled={isRegisterProductSubmitting}
                className="px-4 py-2.5 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-medium disabled:opacity-50 flex items-center gap-2"
              >
                {isRegisterProductSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('inventory.registerProduct.submit') || 'Cadastrar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AGC: Manage categories / units modal – add, edit, remove custom categories and units */}
      {manageCategoriesUnitsMode === 'categories' && (
        <ManageCategoriesUnitsModal
          isOpen
          onClose={() => setManageCategoriesUnitsMode(null)}
          mode="categories"
          builtInItems={builtInCategoriesForModal}
          customItems={customCategories}
          onAdd={handleAddCustomCategory}
          onEdit={handleEditCustomCategory}
          onRemove={handleRemoveCustomCategory}
          t={t}
        />
      )}
      {manageCategoriesUnitsMode === 'units' && (
        <ManageCategoriesUnitsModal
          isOpen
          onClose={() => setManageCategoriesUnitsMode(null)}
          mode="units"
          builtInItems={BUILT_IN_UNITS}
          customItems={customUnits}
          onAdd={handleAddCustomUnit}
          onEdit={handleEditCustomUnit}
          onRemove={handleRemoveCustomUnit}
          t={t}
        />
      )}

      {/* AGC: Quote Builder Modal – solicitar cotação (empty + add from tasks + supplier + WhatsApp) */}
      <QuoteBuilderModal
        isOpen={isQuoteBuilderOpen}
        onClose={() => setIsQuoteBuilderOpen(false)}
        farmName={currentFarm?.name || 'Minha Fazenda'}
        farmLocation={currentFarm?.location ? { lat: currentFarm.location.lat, lng: currentFarm.location.lng } : undefined}
        lotName={selectedLotId === 'all' ? (t('inventory.allLots') || 'Todos os Lotes') : (activeLots.find(l => l.id === selectedLotId)?.name || 'Lote')}
        lotArea={activeLots.reduce((sum, lot) => sum + (lot.area || 0), 0)}
        tasksWithMissing={tasksWithMissing}
        allTaskOptions={allTaskOptions}
        allMissingItems={quoteAllMissingItems}
        allItems={quoteAllItems}
        getQuoteTranslations={getQuoteTranslations}
        onQuoteCreated={() => toast.success(t('inventory.quoteBuilder.savedSuccess') || 'Cotação salva!')}
      />

      {/* AGC: Product Detection Modal (Camera-based) */}
      <ScanInvoiceModal
        isOpen={isScanInvoiceModalOpen}
        onClose={() => setIsScanInvoiceModalOpen(false)}
        farmId={currentFarm?.id}
        userId={user?.uid}
        inventoryItems={inventoryItems}
        onProductConfirmed={(productId, quantity, isNew, prefillData) => {
          // AGC: Handle product confirmed from camera scan
          if (isNew && prefillData) {
            // AGC: Pre-fill the standard register modal with detection data
            setRegisterProductForm({
              name: (prefillData.name as string) || '',
              category: (prefillData.category as string) || 'other',
              unit: (prefillData.unit as string) || 'L',
              initialQuantity: (prefillData.initialQuantity as number) || 0,
              minimumStock: 0,
              costPerUnit: (prefillData.costPerUnit as number) || 0,
            })
            setIsRegisterProductOpen(true)
          } else if (isNew) {
            setIsRegisterProductOpen(true)
          } else if (productId && quantity > 0 && recordMovement) {
            // AGC: Add stock to existing product via movement (include price if provided)
            const movementData: { type: 'entry'; quantity: number; reason: string; costPerUnit?: number; totalCost?: number } = {
              type: 'entry',
              quantity,
              reason: t('productDetection.addedStock', 'Added via camera scan'),
            }
            if (prefillData?.costPerUnit && typeof prefillData.costPerUnit === 'number') {
              movementData.costPerUnit = prefillData.costPerUnit
              movementData.totalCost = prefillData.costPerUnit * quantity
            }
            recordMovement(productId, movementData).then(() => {
              toast.success(t('productDetection.addedStock', { qty: quantity, unit: '', product: '' }))
              handleRefresh()
            }).catch((err: unknown) => {
              console.error('AGC: Error adding stock from scan:', err)
            })
          }
          setIsScanInvoiceModalOpen(false)
        }}
      />
      
      {/* AGC: Order Builder Modal */}
      <OrderBuilderModal
        isOpen={isOrderBuilderOpen}
        onClose={() => setIsOrderBuilderOpen(false)}
        items={filteredItems.map(item => ({
          inputId: item.inputId || item.id,
          name: item.name,
          category: item.category,
          unit: item.unit,
          totalRequired: item.totalRequired || 0,
          inStock: item.inStock,
          needToBuy: item.needToBuy || 0,
          status: item.status,
          lotsNeeding: item.lotsNeeding || [],
        }))}
        onOrderCreated={async () => {
          await handleRefresh()
          await fetchPendingOrders()
        }}
      />
      
      {/* AGC: Order detail / confirmation lives on /pedidos via OrderDrawer. The toolbar's
          Pedidos button routes there. */}

      {/* AGC: Supply Detail Modal - for viewing item details and history */}
      {selectedSupplyItem && (
        <SupplyDetailModal
          isOpen={isSupplyDetailOpen}
          onClose={() => {
            setIsSupplyDetailOpen(false)
            setSelectedSupplyItem(null)
          }}
          inputId={selectedSupplyItem.inputId || selectedSupplyItem.id}
          inputName={selectedSupplyItem.name}
          category={selectedSupplyItem.category}
          unit={selectedSupplyItem.unit}
          currentStock={selectedSupplyItem.inStock}
          inventoryItemId={selectedSupplyItem.inventoryItemId}
          minimumStock={selectedSupplyItem.minimumStock}
          onStockUpdated={handleRefresh}
        />
      )}

      {/* AGC: Manage Warehouses Modal */}
      <ManageWarehousesModal
        isOpen={isManageWarehousesOpen}
        onClose={() => setIsManageWarehousesOpen(false)}
        warehouses={warehouses}
        warehouseHook={warehouseHook}
      />

      {/* AGC: Transfer Stock Modal */}
      <TransferStockModal
        isOpen={isTransferStockOpen}
        onClose={() => setIsTransferStockOpen(false)}
        warehouses={warehouses}
        inventoryItems={inventoryItems}
        warehouseHook={warehouseHook}
      />

      {/* AGC: v3.12.5 — Necessário drill-down sheet. Fires when user taps the red
          AGC: "Necessário/Tarefas Pendentes" row on a tank/silo card. */}
      {(() => {
        if (!breakdownProductId) return null
        const item = inventoryItems.find(i => i.id === breakdownProductId)
        const rows = necessarioBreakdownByProductId.get(breakdownProductId) || []
        return (
          <NecessarioBreakdownSheet
            isOpen={true}
            onClose={() => setBreakdownProductId(null)}
            productName={item?.name || '—'}
            unit={item?.unit || (rows[0]?.unit) || ''}
            rows={rows}
            onRowSaved={() => { refetchTasks() }}
          />
        )
      })()}
    </div>
  )
}

export default UnifiedInventoryPage
